import { Dispatch, memo, SetStateAction, useCallback, useState } from "react";
import { Textarea } from "@mantine/core";
import { SelectPicker } from "rsuite";

import GalleryPreviewModal from "../../../views/library/LibraryComponents/FileInformation/GalleryPreviewModal";
import useUpdateFile from "../../../api/api-hooks/images/useUpdateFile";
import { FileRecord } from "../../../views/library/Library";
import BrieGPTVoiceRecorder from "./BrieGPTVoiceRecorder";
import { newToastEvent } from "../../../utils/ToastUtil";
import BrieGPTUploadButton from "./BrieGPTUploadButton";
import IconButton from "../../../elements/IconButton";
import TenantAPI from "../../../api/api/TenantAPI";
import BrieGPTFileIcon from "./BrieGPTFileIcon";
import { CustomFile } from "./PageViewBrieGPT";
import useBrieGPTModels from "./useBrieGPTModels";

export default function BrieGPTInput({
  files,
  setFiles,
  messageRef,
  model,
  setModel,
  processing,
  handleSendMessage,
  message,
  onStop,
  chatID,
}: {
  files: CustomFile[] | undefined;
  messageRef: React.RefObject<HTMLTextAreaElement>;
  model: string;
  processing: boolean;
  message?: string;
  setFiles: Dispatch<SetStateAction<CustomFile[]>> | undefined;
  setModel: Dispatch<SetStateAction<string>>;
  handleSendMessage: (providedMessage?: string) => void;
  onStop?: () => void;
  chatID: string | null;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const [openInfo, setOpenInfo] = useState<FileRecord | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);

  const { models, modelsLoading } = useBrieGPTModels({ model, setModel });
  const { updateFile, updatingFile } = useUpdateFile();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter") {
        if (e.shiftKey) {
          // Shift + Enter: Add a new line
          e.preventDefault();

          messageRef.current!.value += "\n";
        } else {
          // Enter: Submit the message
          e.preventDefault();
          handleSendMessage();
        }
      }
    },
    [handleSendMessage, messageRef]
  );

  return (
    <>
      <div className="tw:p-4 tw:rounded-xl tw:flex tw:flex-col tw:items-end tw:max-w-full tw:w-full tw:bg-white tw:border-superlight tw:border-2">
        <div className="tw:self-start tw:flex tw:gap-x-2 tw:gap-y-1 tw:flex-wrap">
          {files &&
            setFiles &&
            files.length > 0 &&
            files.map((file, i) => {
              return (
                <BrieGPTFileIcon
                  key={i}
                  fileName={file?.file?.name}
                  fileType={file?.file?.type}
                  uploading={file?.uploading}
                  onClick={file.uploading ? undefined : () => setSelected(i)}
                  onDelete={() => {
                    setSelected(null);
                    const finalChatID = chatID !== "new" ? chatID ?? "" : "";
                    const foundFileIndex = files.findIndex(
                      (f) => f.uploadID === file.uploadID
                    );
                    const foundFile = files[foundFileIndex];

                    if (!foundFile) {
                      return newToastEvent("error", "File not found.");
                    }

                    const uploadedRecordID =
                      foundFile?.uploadedFileData?.RecordID;

                    if (!uploadedRecordID) {
                      return newToastEvent("error", "File not found.");
                    }

                    setFiles((prev) =>
                      prev.filter((f) => f.uploadID !== file.uploadID)
                    );

                    TenantAPI.deleteFiles({
                      type: "fileID",
                      fileID: uploadedRecordID,
                      moduleAPIName: !!finalChatID ? "BrieGPTChats" : "",
                      fieldAPIName: !!finalChatID ? "Attachments" : "",
                      recordID: finalChatID,
                    }).catch((err: any) => {
                      if (err?.status === 409) {
                        // Silently ignore error (File is being used by another record)
                        return;
                      }

                      newToastEvent("error", "Failed to delete file.");
                      setFiles((prev) => {
                        const newFiles = [...prev];

                        // Readd the file that was deleted
                        newFiles.splice(foundFileIndex, 0, foundFile);

                        return newFiles;
                      });
                    });
                  }}
                />
              );
            })}
          {message && (
            <div className="tw:font-medium tw:text-[13px] tw:px-1 tw:mb-2 tw:text-primary">
              {message}
            </div>
          )}
        </div>
        <Textarea
          ref={messageRef}
          placeholder="Send a message to BrieGPT"
          className="tw:w-full tw:focus-within:outline-none"
          autosize
          minRows={2}
          maxRows={6}
          onKeyDown={handleKeyDown}
        />
        <div className="tw:flex tw:w-full tw:items-center tw:justify-between tw:mt-2 tw:text-medium tw:font-bold">
          <div className="tw:flex tw:items-center tw:gap-2">
            <SelectPicker
              cleanable={false}
              value={model}
              onChange={(value) => setModel(value as string)}
              data={models}
              labelKey="ModelName"
              valueKey="ModelAPIName"
              groupBy="Company"
              loading={modelsLoading}
              placeholder="Select a model"
              placement="autoVerticalStart"
              preventOverflow
              searchable={false}
              className="tw:w-48"
            />
            {files && setFiles && !isRecording && (
              <BrieGPTUploadButton files={files} setFiles={setFiles} />
            )}
          </div>
          <div className="tw:flex tw:items-center">
            {!processing && (
              <BrieGPTVoiceRecorder
                isRecording={isRecording}
                setIsRecording={setIsRecording}
                isProcessingRecording={isProcessingRecording}
                setIsProcessingRecording={setIsProcessingRecording}
                handleSendMessage={handleSendMessage}
              />
            )}
            {!isRecording && !isProcessingRecording && (
              <StopStartButtonMemo
                processing={processing}
                onStop={onStop}
                handleSendMessage={handleSendMessage}
                chatID={chatID}
              />
            )}
          </div>
        </div>
      </div>
      <GalleryPreviewModal
        record={null}
        deleteFile={undefined}
        // @ts-expect-error
        files={
          Array.isArray(files)
            ? files?.flatMap((f) => {
                const uploadedData = f?.uploadedFileData;
                return uploadedData ? { File: uploadedData } : [];
              })
            : []
        }
        selected={selected || 0}
        isOpen={typeof selected === "number"}
        submitting={updatingFile}
        closeModal={() => setSelected(null)}
        nextFile={() =>
          setSelected((prev) => (typeof prev === "number" ? prev + 1 : null))
        }
        previousFile={() =>
          setSelected((prev) => (typeof prev === "number" ? prev - 1 : null))
        }
        handleUpdateFile={handleUpdateFile}
        setSelected={setSelected}
        setOpenInfo={setOpenInfo}
        openInfo={openInfo}
      />
    </>
  );

  function handleUpdateFile(
    newValue: string,
    key: string,
    onSuccess?: () => void
  ) {
    if (!files || typeof selected !== "number") return;

    updateFile({
      payload: { [key]: newValue },
      fileID: files[selected]?.uploadedFileData?.RecordID,
      onSuccess: () => {
        setFiles &&
          setFiles((prev) => {
            return prev.map((f, i) => {
              if (i === selected) {
                const newF = structuredClone(f);

                newF.uploadedFileData[key] = newValue;

                return newF;
              }

              return f;
            });
          });

        onSuccess && onSuccess();
      },
    });
  }
}

const StopStartButton = ({
  processing,
  onStop,
  handleSendMessage,
  chatID,
}: {
  processing: boolean;
  onStop?: () => void;
  handleSendMessage: () => void;
  chatID: string | null;
}) => {
  return (
    <IconButton
      icon={processing ? "stop" : "send"}
      color="primary"
      size="lg"
      onClick={processing ? onStop : handleSendMessage}
      disabled={processing && !chatID} // While a chat is being created, the stop button is disabled.
    />
  );
};

const StopStartButtonMemo = memo(StopStartButton);
